import { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useSearchParams } from 'react-router';
import { CheckCircle, X } from 'lucide-react';
import { setReferral } from '../../store/loanApplicationSlice';
import { supabase } from '../../lib/supabase';

/**
 * WelcomeCheck — slim, dismissible top banner for referral acknowledgment only.
 * Progress is saved after account creation; no anonymous resume prompt.
 */
const WelcomeCheck = () => {
  const dispatch = useDispatch();
  const [searchParams] = useSearchParams();
  const existingReferrer = useSelector(state => state.loanApplication.referrer);

  const [dismissed, setDismissed] = useState(false);
  const [referrerInfo, setReferrerInfo] = useState(null);

  useEffect(() => {
    const refCode = searchParams.get('ref');
    if (!refCode || existingReferrer) return;

    const lookup = async () => {
      try {
        const { data: partner } = await supabase
          .from('referral_partners')
          .select('*')
          .eq('referral_code', refCode.toUpperCase())
          .eq('status', 'active')
          .single();

        if (!partner) return;

        setReferrerInfo(partner);

        const { data: referral } = await supabase
          .from('referrals')
          .insert({
            partner_id: partner.id,
            referral_code_used: refCode.toUpperCase(),
            status: 'clicked',
            utm_source: searchParams.get('utm_source'),
            utm_medium: searchParams.get('utm_medium'),
            utm_campaign: searchParams.get('utm_campaign'),
          })
          .select()
          .single();

        if (referral) {
          dispatch(setReferral({
            referralCode: refCode.toUpperCase(),
            referrer: partner,
            referralId: referral.id,
          }));
        }
      } catch (err) {
        console.warn('Referral lookup failed:', err?.message);
      }
    };

    lookup();
  }, [searchParams, existingReferrer, dispatch]);

  const showBanner = !dismissed && (referrerInfo || existingReferrer);
  if (!showBanner) return null;

  const partner = referrerInfo || existingReferrer;

  return (
    <div style={{
      backgroundColor: 'var(--lk-success-50)',
      borderBottom: '1px solid var(--lk-success-200)',
      padding: 'var(--lk-space-3) var(--lk-space-4)',
    }}>
      <div style={{
        maxWidth: 1200, margin: '0 auto',
        display: 'flex', alignItems: 'center', gap: 12,
      }}>
        <CheckCircle size={20} color="var(--lk-success-600)" />
        <div style={{ flex: 1, fontSize: 'var(--lk-text-sm)' }}>
          <strong>
            Referred by {partner.first_name} {partner.last_name}
          </strong>
          {partner.company_name ? ` · ${partner.company_name}` : ''}
          <span style={{ marginLeft: 8, color: 'var(--lk-text-tertiary)' }}>
            They&apos;ll get a copy of any packet you forward through us.
          </span>
        </div>
        <button
          type="button"
          onClick={() => setDismissed(true)}
          aria-label="Dismiss"
          style={{
            background: 'none', border: 'none', cursor: 'pointer',
            padding: 4, color: 'var(--lk-text-tertiary)',
          }}
        >
          <X size={16} />
        </button>
      </div>
    </div>
  );
};

export default WelcomeCheck;
